import React, { useState, useEffect } from 'react';
import { Barcode, Download, Settings, Printer, Copy, X } from 'lucide-react';
import PageIdentifier from '../shared/PageIdentifier';
import SCREEN_IDS from '../../utils/screenIds';
import * as XLSX from 'xlsx';
import { todayCentral } from '../../shared/utils/dateUtils';

const BarcodeGenerator = () => {
  const [activeTab, setActiveTab] = useState('generate'); // generate, preview, print, history
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [barcodeFormat, setBarcodeFormat] = useState('code128'); // code128, code39, ean13, ean8, upca, qrcode
  const [barcodeSize, setBarcodeSize] = useState('medium'); // small, medium, large
  const [showLabel, setShowLabel] = useState(true);
  const [generatedBarcodes, setGeneratedBarcodes] = useState([]);
  const [printSettings, setPrintSettings] = useState({
    paperSize: 'a4',
    orientation: 'portrait',
    margin: 10,
    barcodePerPage: 12
  });
  const [history, setHistory] = useState([]);
  const [tagLoading, setTagLoading] = useState(false);

  // Download an EPL file per selected item; the local print agent watches the
  // downloads folder and prints each one. Selection is cleared when done.
  const handlePrintTags = async () => {
    if (selectedItems.length === 0) {
      alert('Please select at least one item');
      return;
    }
    try {
      setTagLoading(true);
      for (const item of selectedItems) {
        const res = await fetch(`/api/labels/epl/${item.id}`, { credentials: 'include' });
        if (!res.ok) continue;
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${item.code || item.id}.epl`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
      setSelectedItems([]);
    } catch (e) {
      alert('Failed to send tags: ' + e.message);
    } finally {
      setTagLoading(false);
    }
  };

  // Load items on mount
  useEffect(() => {
    fetchAllItems();
    loadGenerationHistory();
  }, []);

  // Fetch all jewelry items
  const fetchAllItems = async () => {
    try {
      const response = await fetch('/api/jewelry', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setItems(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  // Search items
  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      fetchAllItems();
      return;
    }

    try {
      const response = await fetch(`/api/jewelry?search=${encodeURIComponent(query)}&searchMode=starts_with&sortBy=code&order=ASC&limit=1000`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setItems(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error searching items:', error);
    }
  };

  // Enter in the search box adds an exact code match (supports barcode scanners).
  const handleSearchKey = (e) => {
    if (e.key !== 'Enter') return;
    const q = searchQuery.trim().toUpperCase();
    if (!q) return;
    const match = items.find(i => (i.code || '').toUpperCase() === q);
    if (match) {
      handleAddItem(match.code);
      setSearchQuery('');
      fetchAllItems();
    }
  };

  // Add single item for barcode generation
  const handleAddItem = async (code) => {
    try {
      const response = await fetch(`/api/jewelry/details/${code}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        alert(`Item ${code} not found`);
        return;
      }

      const item = await response.json();

      // Check if already added
      if (selectedItems.some(i => i.code === code)) {
        alert('Item already added');
        return;
      }

      setSelectedItems([...selectedItems, item]);
    } catch (error) {
      console.error('Error adding item:', error);
      alert('Error fetching item details');
    }
  };

  // Add multiple items from search results
  const handleAddSelected = () => {
    const itemsToAdd = items.filter(item => 
      !selectedItems.some(s => s.code === item.code)
    );
    
    if (itemsToAdd.length === 0) {
      alert('No new items to add');
      return;
    }

    setSelectedItems([...selectedItems, ...itemsToAdd]);
    setSearchQuery('');
    fetchAllItems();
  };

  // Remove item from selection
  const removeItem = (code) => {
    setSelectedItems(selectedItems.filter(item => item.code !== code));
  };

  // Generate barcodes
  const handleGenerateBarcodes = async () => {
    if (selectedItems.length === 0) {
      alert('Please select at least one item');
      return;
    }

    try {
      setLoading(true);
      const barcodes = selectedItems.map(item => ({
        code: item.code,
        name: item.name,
        category: item.category_name,
        vendor: item.vendor_name,
        weight: item.fine_net_weight ?? item.net_weight, // Fine Net Wt (gross − stones)
        format: barcodeFormat,
        size: barcodeSize,
        timestamp: new Date().toLocaleString(),
        id: `${item.code}-${Date.now()}`
      }));

      setGeneratedBarcodes(barcodes);
      addToHistory(barcodes);
      setActiveTab('preview');
    } catch (error) {
      console.error('Error generating barcodes:', error);
      alert('Error generating barcodes');
    } finally {
      setLoading(false);
    }
  };

  // Add to generation history
  const addToHistory = (barcodes) => {
    const newEntry = {
      id: Date.now(),
      count: barcodes.length,
      format: barcodeFormat,
      timestamp: new Date().toLocaleString(),
      items: barcodes.map(b => b.code)
    };
    setHistory([newEntry, ...history.slice(0, 19)]);
  };

  // Load generation history
  const loadGenerationHistory = () => {
    const saved = localStorage.getItem('barcodeHistory');
    if (saved) {
      try {
        setHistory(JSON.parse(saved));
      } catch (error) {
        console.error('Error loading history:', error);
      }
    }
  };

  // Export barcodes as CSV
  const exportToCSV = () => {
    if (generatedBarcodes.length === 0) {
      alert('No barcodes to export');
      return;
    }

    const data = generatedBarcodes.map(b => ({
      'Barcode Code': b.code,
      'Item Name': b.name,
      'Category': b.category,
      'Vendor': b.vendor,
      'Weight': b.weight,
      'Format': b.format,
      'Generated': b.timestamp
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Barcodes');
    XLSX.writeFile(wb, `barcodes_${todayCentral()}.xlsx`);
  };

  // Print barcodes
  const handlePrint = () => {
    if (generatedBarcodes.length === 0) {
      alert('No barcodes to print');
      return;
    }

    const printWindow = window.open('', '', 'width=800,height=600');
    let html = `
      <html>
      <head>
        <title>Barcode Print</title>
        <style>
          body { font-family: Arial, sans-serif; margin: ${printSettings.margin}mm; }
          .barcode-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; }
          .barcode-item { 
            border: 1px solid #ccc; 
            padding: 10px; 
            text-align: center;
            page-break-inside: avoid;
          }
          .barcode-canvas { max-width: 100%; height: auto; }
          .barcode-label { font-size: 10px; margin-top: 5px; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>
    `;

    generatedBarcodes.forEach(barcode => {
      html += `
        <div class="barcode-item">
          <div id="barcode-${barcode.id}"></div>
          ${showLabel ? `<div class="barcode-label"><strong>${barcode.code}</strong><br/>${barcode.name}</div>` : ''}
        </div>
      `;
    });

    html += `
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();

    // Load JsBarcode library
    const script = printWindow.document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js';
    script.onload = () => {
      generatedBarcodes.forEach(barcode => {
        try {
          window.JsBarcode(`#barcode-${barcode.id}`, barcode.code, {
            format: barcode.format.toUpperCase(),
            width: getBarcodeWidth(),
            height: getBarcodeHeight()
          });
        } catch (error) {
          console.error(`Error generating barcode for ${barcode.code}:`, error);
        }
      });
      
      setTimeout(() => {
        printWindow.print();
      }, 1000);
    };
    printWindow.document.head.appendChild(script);
  };

  // Copy barcode code to clipboard
  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    alert(`Copied: ${code}`);
  };

  // Get barcode width based on size
  const getBarcodeWidth = () => {
    switch (barcodeSize) {
      case 'small': return 1;
      case 'medium': return 2;
      case 'large': return 3;
      default: return 2;
    }
  };

  // Get barcode height based on size
  const getBarcodeHeight = () => {
    switch (barcodeSize) {
      case 'small': return 40;
      case 'medium': return 60;
      case 'large': return 80;
      default: return 60;
    }
  };

  // Render barcode preview
  const BarcodePreview = ({ barcode }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col items-center">
      <div className="w-full h-24 bg-gray-100 flex items-center justify-center mb-2">
        <svg width="200" height="100" viewBox="0 0 200 100">
          {/* Simple barcode representation */}
          {[...Array(40)].map((_, i) => (
            <rect
              key={i}
              x={i * 5}
              y={10}
              width={Math.random() > 0.5 ? 3 : 2}
              height={80}
              fill={Math.random() > 0.5 ? '#000' : '#fff'}
            />
          ))}
        </svg>
      </div>
      <div className="text-xs font-mono text-center w-full">
        <div className="font-bold">{barcode.code}</div>
        <div className="text-gray-600">{barcode.name}</div>
        <div className="text-gray-500 text-xs mt-1">{barcode.format.toUpperCase()}</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <PageIdentifier screenId={SCREEN_IDS.UTILITIES?.BARCODE_GENERATOR || 'UTL-007'} />

        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
            <Barcode className="text-blue-600" size={32} />
            Barcode & QR Code Generator
          </h1>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {[
            { id: 'generate', label: '🔧 Generate' },
            { id: 'preview', label: '👁️ Preview' },
            { id: 'print', label: '🖨️ Print' },
            { id: 'history', label: '📋 History' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Generate Tab */}
        {activeTab === 'generate' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Panel - Search & Selection */}
            <div className="lg:col-span-2 space-y-6">
              {/* Search Items */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Search & Add Items</h2>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Search Items by Product Code Prefix
                    </label>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      onKeyPress={handleSearchKey}
                      placeholder="Start typing a product code, for example RNG-"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {items.length > 0 && (
                    <>
                      <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                        {items.map(item => (
                          <div key={item.code} className="p-3 border-b hover:bg-gray-50 flex justify-between items-center">
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-sm">{item.code}</div>
                              <div className="text-xs text-gray-600">{item.name}</div>
                            </div>
                            <button
                              onClick={() => handleAddItem(item.code)}
                              disabled={selectedItems.some(s => s.code === item.code)}
                              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                                selectedItems.some(s => s.code === item.code)
                                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                  : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                              }`}
                            >
                              {selectedItems.some(s => s.code === item.code) ? '✓ Added' : '+ Add'}
                            </button>
                          </div>
                        ))}
                      </div>

                      {items.length > 0 && (
                        <button
                          onClick={handleAddSelected}
                          className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          Add All Search Results ({items.length})
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Right Panel - Settings */}
            <div className="space-y-6">
              {/* Barcode Settings */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Settings size={20} />
                  Barcode Settings
                </h2>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Format
                    </label>
                    <select
                      value={barcodeFormat}
                      onChange={(e) => setBarcodeFormat(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="code128">Code 128</option>
                      <option value="code39">Code 39</option>
                      <option value="ean13">EAN-13</option>
                      <option value="ean8">EAN-8</option>
                      <option value="upca">UPC-A</option>
                      <option value="qrcode">QR Code</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Size
                    </label>
                    <select
                      value={barcodeSize}
                      onChange={(e) => setBarcodeSize(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="small">Small (40px)</option>
                      <option value="medium">Medium (60px)</option>
                      <option value="large">Large (80px)</option>
                    </select>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="showLabel"
                      checked={showLabel}
                      onChange={(e) => setShowLabel(e.target.checked)}
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                    <label htmlFor="showLabel" className="ml-2 text-sm text-gray-700">
                      Show Item Code & Name
                    </label>
                  </div>
                </div>
              </div>

              {/* Selected Items Count */}
              <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded">
                <div className="text-2xl font-bold text-blue-600">{selectedItems.length}</div>
                <div className="text-sm text-gray-700">Items Selected</div>
              </div>

              {/* Generate Button */}
              <button
                onClick={handleGenerateBarcodes}
                disabled={selectedItems.length === 0 || loading}
                className={`w-full py-3 rounded-lg font-medium transition-colors ${
                  selectedItems.length === 0 || loading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {loading ? 'Generating...' : `Generate ${selectedItems.length} Barcodes`}
              </button>

              <button
                onClick={handlePrintTags}
                disabled={selectedItems.length === 0 || tagLoading}
                className={`w-full py-3 rounded-lg font-medium transition-colors ${
                  selectedItems.length === 0 || tagLoading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-emerald-600 text-white hover:bg-emerald-700'
                }`}
              >
                {tagLoading ? 'Loading...' : `Print ${selectedItems.length} Tags (2x1)`}
              </button>

              {/* Selected Items List */}
              {selectedItems.length > 0 && (
                <div className="bg-white rounded-lg shadow-md p-4">
                  <h3 className="font-semibold text-gray-800 mb-3">Selected Items</h3>
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {selectedItems.map(item => (
                      <div key={item.code} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <div className="flex-1 min-w-0">
                          <div className="font-mono text-sm font-bold">{item.code}</div>
                          <div className="text-xs text-gray-600 truncate">{item.name}</div>
                        </div>
                        <button
                          onClick={() => removeItem(item.code)}
                          className="ml-2 p-1 text-red-600 hover:bg-red-50 rounded"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Preview Tab */}
        {activeTab === 'preview' && (
          <div className="space-y-6">
            {generatedBarcodes.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <Barcode size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 mb-4">No barcodes generated yet</p>
                <button
                  onClick={() => setActiveTab('generate')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Generate Barcodes
                </button>
              </div>
            ) : (
              <>
                <div className="flex gap-2">
                  <button
                    onClick={handlePrint}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <Printer size={20} />
                    Print Barcodes
                  </button>
                  <button
                    onClick={exportToCSV}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Download size={20} />
                    Export to Excel
                  </button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {generatedBarcodes.map(barcode => (
                    <div key={barcode.id} className="relative">
                      <BarcodePreview barcode={barcode} />
                      <button
                        onClick={() => copyToClipboard(barcode.code)}
                        className="absolute top-2 right-2 p-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                        title="Copy barcode code"
                      >
                        <Copy size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Print Tab */}
        {activeTab === 'print' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">Print Settings</h2>

            <div className="grid grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Paper Size
                </label>
                <select
                  value={printSettings.paperSize}
                  onChange={(e) => setPrintSettings({...printSettings, paperSize: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="a4">A4</option>
                  <option value="letter">Letter</option>
                  <option value="a5">A5</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Orientation
                </label>
                <select
                  value={printSettings.orientation}
                  onChange={(e) => setPrintSettings({...printSettings, orientation: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="portrait">Portrait</option>
                  <option value="landscape">Landscape</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Margin (mm)
                </label>
                <input
                  type="number"
                  value={printSettings.margin}
                  onChange={(e) => setPrintSettings({...printSettings, margin: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Barcodes per Page
                </label>
                <select
                  value={printSettings.barcodePerPage}
                  onChange={(e) => setPrintSettings({...printSettings, barcodePerPage: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value={6}>6 (2x3)</option>
                  <option value={12}>12 (3x4)</option>
                  <option value={20}>20 (4x5)</option>
                </select>
              </div>
            </div>

            <button
              onClick={handlePrint}
              disabled={generatedBarcodes.length === 0}
              className={`w-full py-3 rounded-lg font-medium transition-colors ${
                generatedBarcodes.length === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              {generatedBarcodes.length === 0 ? 'No Barcodes to Print' : `Print ${generatedBarcodes.length} Barcodes`}
            </button>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">Generation History</h2>

            {history.length === 0 ? (
              <p className="text-gray-600 text-center py-8">No generation history yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Date & Time</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Count</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Format</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Items</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map(entry => (
                      <tr key={entry.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 text-sm">{entry.timestamp}</td>
                        <td className="py-3 px-4 text-sm font-semibold">{entry.count}</td>
                        <td className="py-3 px-4 text-sm">{entry.format}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{entry.items.join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BarcodeGenerator;
